import { useState, useMemo } from "react";
import { WIDGET_REGISTRY } from "@/lib/dashboard/widgetRegistry";
import { WidgetCategory, DashboardWidgetItem } from "@/types/dashboard";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Search, Plus, Check, Sparkles, Filter } from "lucide-react";

interface WidgetLibraryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  activeLayout: DashboardWidgetItem[];
  onAddWidget: (widgetId: string) => void;
  onRemoveWidget: (widgetId: string) => void;
}

const CATEGORIES: { key: string; label: string }[] = [
  { key: "all", label: "All Widgets" },
  { key: "financial", label: "Financial KPIs" },
  { key: "charts", label: "Charts & Trends" },
  { key: "operations", label: "Operations" },
  { key: "inventory", label: "Inventory" },
  { key: "insights", label: "Insights" },
  { key: "gst", label: "GST & Tax" },
  { key: "ai", label: "AI Intelligence" },
];

export const WidgetLibraryModal = ({
  open,
  onOpenChange,
  activeLayout,
  onAddWidget,
  onRemoveWidget,
}: WidgetLibraryModalProps) => {
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  const activeWidgetIds = useMemo(() => {
    return new Set(activeLayout.filter((w) => w.visible).map((w) => w.widgetId));
  }, [activeLayout]);

  const filteredWidgets = useMemo(() => {
    const list = Object.values(WIDGET_REGISTRY);
    const q = search.toLowerCase().trim();

    return list.filter((w) => {
      const matchCat = selectedCategory === "all" || w.category === selectedCategory;
      const matchSearch =
        !q ||
        w.title.toLowerCase().includes(q) ||
        w.description.toLowerCase().includes(q) ||
        w.category.toLowerCase().includes(q);
      return matchCat && matchSearch;
    });
  }, [search, selectedCategory]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col p-6">
        <DialogHeader className="pb-3 border-b border-border/50">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <DialogTitle className="text-lg font-bold text-foreground">Widget Library</DialogTitle>
          </div>
          <DialogDescription className="text-xs text-muted-foreground">
            Personalize your Business Command Center. Add or remove widgets to customize your workflow.
          </DialogDescription>
        </DialogHeader>

        {/* Search & Filter Bar */}
        <div className="flex flex-col sm:flex-row gap-3 my-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search widgets by name or description..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-9 text-xs"
            />
          </div>
        </div>

        {/* Categories Tab Strip */}
        <Tabs value={selectedCategory} onValueChange={setSelectedCategory} className="w-full">
          <TabsList className="w-full justify-start overflow-x-auto h-9 bg-muted/60 p-1 flex-nowrap scrollbar-none">
            {CATEGORIES.map((cat) => (
              <TabsTrigger key={cat.key} value={cat.key} className="text-xs px-3 py-1 shrink-0">
                {cat.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {/* Widgets Grid */}
        <div className="flex-1 overflow-y-auto pr-1 py-3 grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[50vh]">
          {filteredWidgets.map((widget) => {
            const isAdded = activeWidgetIds.has(widget.widgetId);
            const Icon = widget.icon;

            return (
              <div
                key={widget.widgetId}
                className={`p-3.5 rounded-xl border transition-all flex flex-col justify-between ${
                  isAdded
                    ? "bg-primary/[0.03] border-primary/40 shadow-sm"
                    : "bg-card border-border hover:border-primary/30"
                }`}
              >
                <div>
                  <div className="flex items-start justify-between gap-2 mb-1.5">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                        <Icon className="h-4 w-4" />
                      </div>
                      <span className="text-xs font-bold text-foreground">{widget.title}</span>
                    </div>
                    <Badge variant="outline" className="text-[10px] capitalize px-1 py-0 shrink-0">
                      {widget.category}
                    </Badge>
                  </div>
                  <p className="text-[11px] text-muted-foreground leading-relaxed line-clamp-2">
                    {widget.description}
                  </p>
                </div>

                <div className="flex items-center justify-between pt-3 mt-2 border-t border-border/40 text-xs">
                  <span className="text-[10px] text-muted-foreground uppercase tracking-wider">
                    Default: {widget.defaultWidth}
                  </span>

                  {isAdded ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onRemoveWidget(widget.widgetId)}
                      className="h-7 text-xs gap-1 border-primary/40 text-primary hover:bg-destructive/10 hover:text-destructive hover:border-destructive/30"
                    >
                      <Check className="h-3 w-3" />
                      <span>Added</span>
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => onAddWidget(widget.widgetId)}
                      className="h-7 text-xs gap-1"
                    >
                      <Plus className="h-3 w-3" />
                      <span>Add Widget</span>
                    </Button>
                  )}
                </div>
              </div>
            );
          })}

          {filteredWidgets.length === 0 && (
            <div className="col-span-full py-12 text-center text-xs text-muted-foreground">
              No widgets match your search criteria.
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
